import React, { useEffect, useState, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MaterialTable from "../components/MaterialTableAdapter";
import { toast } from "react-toastify";
import axios from "axios";
import {
  CheckCheck,
  CheckCircle2,
  FileDown,
  FileSpreadsheet,
  Plus,
  Trash2,
  XCircle,
} from "lucide-react";
import { FilterPanel, FilterToggle } from "../ui";

import Input from "../components/Input";
import Button from "../components/Button";
import Loading from "../components/Loading";
import AddBox from "@mui/icons-material/AddBox";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import Check from "@mui/icons-material/Check";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Clear from "@mui/icons-material/Clear";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Edit from "@mui/icons-material/Edit";
import FilterList from "@mui/icons-material/FilterList";
import FirstPage from "@mui/icons-material/FirstPage";
import LastPage from "@mui/icons-material/LastPage";
import Remove from "@mui/icons-material/Remove";
import SaveAlt from "@mui/icons-material/SaveAlt";
import Search from "@mui/icons-material/Search";
import ViewColumn from "@mui/icons-material/ViewColumn";
import Visibility from "@mui/icons-material/Visibility";
import DateRange from "@mui/icons-material/DateRange";
import {
  addDailyCommission,
  getDailyCommissions,
  deleteDailyCommission,
  getCommissionSummary,
  updateEvidenceStatus,
  exportDailyCommissions,
  bulkUpdateEvidenceStatus,
  getStaffByClub,
  getRakeClubs,
} from "../actions/dailyCommissionActions";
import {
  ADD_DAILY_COMMISSION_RESET,
  DELETE_DAILY_COMMISSION_RESET,
  UPDATE_EVIDENCE_STATUS_RESET,
  BULK_UPDATE_EVIDENCE_STATUS_RESET,
} from "../constants/dailyCommissionConstant";
import { exportToExcel, exportToPDF } from "../utils/exportUtils";
import ScreenHeader from '../components/layout/ScreenHeader'

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const DailyCommission = ({ history }) => {
  // * States
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // * Add Commission Modal
  const [openModal, setOpenModal] = useState(false);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [selectedClubIds, setSelectedClubIds] = useState([]); // Multi-club selection
  // const [rakeType, setRakeType] = useState('percentage') // REMOVED
  // const [rakeValue, setRakeValue] = useState('') // REMOVED
  const [remarks, setRemarks] = useState("");

  // * Table Breakdown States
  const [activeTables, setActiveTables] = useState([]);
  const [tableRakeValues, setTableRakeValues] = useState({});
  const [loadingTables, setLoadingTables] = useState(false);
  const [selectedTableId, setSelectedTableId] = useState(""); // Selected table dropdown
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidencePreview, setEvidencePreview] = useState(null);

  // * Camera Capture States
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);

  // * Evidence Timestamp & Location States
  const [evidenceTimestamp, setEvidenceTimestamp] = useState(null);
  const [evidenceLocationData, setEvidenceLocationData] = useState(null);

  // * Dealer Selection States
  const [availableDealers, setAvailableDealers] = useState([]);
  const [selectedDealerId, setSelectedDealerId] = useState("");
  const [loadingDealers, setLoadingDealers] = useState(false);

  // * Chip Distribution States
  const [chipDistribution, setChipDistribution] = useState({});
  const [calculatedChips, setCalculatedChips] = useState({});
  const [showChipDistribution, setShowChipDistribution] = useState(false);

  // * Chip Distribution View Modal (for viewing chips in table)
  const [chipViewModal, setChipViewModal] = useState({
    open: false,
    data: null,
  });

  // * Evidence Preview Modal
  const [evidenceModal, setEvidenceModal] = useState({
    open: false,
    url: null,
    timestamp: null,
    location: null,
  });

  // * Delete Modal
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedCommissionForDelete, setSelectedCommissionForDelete] =
    useState(null);
  const [
    selectedCommissionsForBulkDelete,
    setSelectedCommissionsForBulkDelete,
  ] = useState([]);

  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [bulkApproveModal, setBulkApproveModal] = useState(false);

  // * Evidence Status Modal
  const [statusModal, setStatusModal] = useState(false);
  const [selectedCommissionForStatus, setSelectedCommissionForStatus] =
    useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [statusRemarks, setStatusRemarks] = useState("");

  // * Filter States
  const [startDate, setStartDate] = useState("");

  const [endDate, setEndDate] = useState("");
  const [datePreset, setDatePreset] = useState("custom"); // 'today', '7days', 'custom'
  const [filterClubIds, setFilterClubIds] = useState([]);
  const [filterEvidenceStatus, setFilterEvidenceStatus] = useState("");
  const [filterRakeType, setFilterRakeType] = useState("");
  const [filterIsActive, setFilterIsActive] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // * Summary States
  const [summary, setSummary] = useState({});

  // * Available Clubs
  const [availableClubs, setAvailableClubs] = useState([]);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [clubsError, setClubsError] = useState("");

  // * Roles & Permissions
  const [canViewRake, setCanViewRake] = useState(false);
  const [canAddRake, setCanAddRake] = useState(false);
  const [canUpdateRake, setCanUpdateRake] = useState(false);
  const [canApproveRake, setCanApproveRake] = useState(false);
  const [canDeleteRake, setCanDeleteRake] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // * Initialization
  const dispatch = useDispatch();

  // * Check for auth
  const userLogin = useSelector((state) => state.adminLogin);
  const { userInfo } = userLogin;

  // * Check for role permissions
  useEffect(() => {
    if (userInfo?.role) {
      const role = userInfo.role;
      const superAdmin = role.isSuperAdmin === true;
      setIsSuperAdmin(superAdmin);

      // Set permissions
      setCanViewRake(superAdmin || role.rakeView === true);
      setCanAddRake(superAdmin || role.rakeAdd === true);
      setCanUpdateRake(superAdmin || role.rakeUpdate === true);
      setCanApproveRake(superAdmin || role.rakeApproval === true);
      setCanDeleteRake(superAdmin); // Only super admin can delete

      // If user cannot view rake, redirect
      if (!superAdmin && role.rakeView !== true) {
        toast("Not Authorized to view Rake Management", {
          type: "error",
          hideProgressBar: true,
          autoClose: 2000,
        });
        navigate("/");
      }
    }
  }, [userInfo, navigate]);

  // * Fetch clubs for rake entry dropdowns and filters
  useEffect(() => {
    const fetchRakeClubOptions = async () => {
      if (!canViewRake || !userInfo?.token) {
        return;
      }

      setLoadingClubs(true);
      setClubsError("");
      try {
        const clubs = await dispatch(getRakeClubs());
        setAvailableClubs(Array.isArray(clubs) ? clubs : []);
      } catch (error) {
        setAvailableClubs([]);
        setClubsError(error.message || "Failed to load clubs");
      } finally {
        setLoadingClubs(false);
      }
    };

    fetchRakeClubOptions();
  }, [canViewRake, dispatch, userInfo?.token]);

  useEffect(() => {
    if (canViewRake) {
      dispatch(getDailyCommissions({}));
      dispatch(getCommissionSummary({}));
    }
  }, [canViewRake, dispatch]);

  // * Fetch Tables when Club Selection Changes
  useEffect(() => {
    const fetchTables = async () => {
      if (selectedClubIds.length === 0) {
        setActiveTables([]);
        setTableRakeValues({});
        setAmount("");
        return;
      }

      setLoadingTables(true);
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        };

        const baseUrl = process.env.REACT_APP_API_URL || "/api";
        // Fetch tables for all selected clubs
        const tablesPromises = selectedClubIds.map((clubId) =>
          axios.get(`${baseUrl}/tables?clubId=${clubId}&status=Active`, config),
        );

        const responses = await Promise.all(tablesPromises);
        // Flatten all tables
        const allTables = responses.flatMap((res) => res.data.data || []);
        setActiveTables(allTables);

        // Reset values on club change to prevent mismatch
        setTableRakeValues({});
        setAmount("");
      } catch (error) {
        console.error(
          "Error fetching tables for clubs:",
          selectedClubIds,
          error,
        );
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch active tables";
        toast(`Error: ${errorMessage}`, { type: "error" });
      } finally {
        setLoadingTables(false);
      }
    };

    if (openModal && userInfo?.token) {
      fetchTables();
    }
  }, [selectedClubIds, userInfo?.token, openModal]);

  // * Fetch Staff (Coordinators) when Club Selection Changes
  useEffect(() => {
    const fetchStaff = async () => {
      if (selectedClubIds.length === 0) {
        setAvailableDealers([]);
        setSelectedDealerId("");
        return;
      }

      setLoadingDealers(true);
      try {
        const staff = await dispatch(getStaffByClub(selectedClubIds));
        setAvailableDealers(staff);
        // Reset coordinator selection when clubs change
        setSelectedDealerId("");
      } catch (error) {
        console.error("Error fetching staff:", error);
        setAvailableDealers([]);
      } finally {
        setLoadingDealers(false);
      }
    };

    if (openModal) {
      fetchStaff();
    }
  }, [selectedClubIds, openModal, dispatch]);

  // * Handle Table Rake Input
  const handleTableRakeChange = (tableId, value) => {
    // value is string, parse to float for sum, but keep string in state for input
    const numValue = value === "" ? 0 : parseFloat(value);

    const newValues = { ...tableRakeValues, [tableId]: value };
    setTableRakeValues(newValues);

    // Calculate total amount
    const total = Object.values(newValues).reduce((sum, curr) => {
      const val = curr === "" ? 0 : parseFloat(curr);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);

    const newAmount = total > 0 ? total.toString() : "";
    setAmount(newAmount);

    // Auto-calculate chip distribution when amount changes
    if (total > 0) {
      const calculated = calculateChipDistributionHelper(total);
      setCalculatedChips(calculated);
      setChipDistribution(calculated);
    } else {
      setCalculatedChips({});
      setChipDistribution({});
    }
  };

  // Helper function for chip calculation (defined before use)
  const calculateChipDistributionHelper = (totalAmount) => {
    const chipDenominations = [25000, 10000, 5000, 1000, 500, 100];
    const distribution = {};
    let remainingAmount = parseFloat(totalAmount) || 0;

    for (const denomination of chipDenominations) {
      if (remainingAmount >= denomination) {
        const chipCount = Math.floor(remainingAmount / denomination);
        distribution[denomination] = chipCount;
        remainingAmount = remainingAmount % denomination;
      }
    }

    return distribution;
  };

  // * ===== CHIP DISTRIBUTION HELPER FUNCTIONS =====

  // Calculate optimal chip distribution for a given amount
  const calculateChipDistribution = (totalAmount) => {
    const chipDenominations = [25000, 10000, 5000, 1000, 500, 100];
    const distribution = {};
    let remainingAmount = parseFloat(totalAmount) || 0;

    for (const denomination of chipDenominations) {
      if (remainingAmount >= denomination) {
        const chipCount = Math.floor(remainingAmount / denomination);
        distribution[denomination] = chipCount;
        remainingAmount = remainingAmount % denomination;
      }
    }

    return distribution;
  };

  // Handle individual chip count change
  const handleChipChange = (denomination, value) => {
    const newChipDistribution = {
      ...chipDistribution,
      [denomination]: parseInt(value) || 0,
    };

    // Remove zero values
    if (parseInt(value) === 0) {
      delete newChipDistribution[denomination];
    }

    setChipDistribution(newChipDistribution);
  };

  // Calculate total value from chip distribution
  const calculateTotalFromChips = () => {
    return Object.entries(chipDistribution).reduce(
      (total, [denomination, count]) => {
        return total + parseInt(denomination) * parseInt(count);
      },
      0,
    );
  };

  // Reset chips to auto-calculated values
  const resetChipsToCalculated = () => {
    const calculated = calculateChipDistribution(amount);
    setCalculatedChips(calculated);
    setChipDistribution(calculated);
  };

  // Toggle chip distribution visibility and auto-calculate when enabled
  const toggleChipDistribution = () => {
    if (!showChipDistribution && amount) {
      // When enabling, auto-calculate chips from current amount
      const calculated = calculateChipDistribution(amount);
      setCalculatedChips(calculated);
      setChipDistribution(calculated);
    }
    setShowChipDistribution(!showChipDistribution);
  };

  // * Get Daily Commissions
  const getDailyCommissionsInfo = useSelector(
    (state) => state.getDailyCommissions,
  );
  const {
    loading: loadingGetCommissions,
    error: errorGetCommissions,
    commissions,
  } = getDailyCommissionsInfo;

  useEffect(() => {
    if (commissions) {
      setLoading(false);
      setData(commissions);
    } else if (errorGetCommissions) {
      setLoading(false);
      toast(errorGetCommissions, {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
    }
  }, [commissions, errorGetCommissions]);

  // * Get Commission Summary
  const getCommissionSummaryInfo = useSelector(
    (state) => state.getCommissionSummary,
  );
  const { summary: summaryData } = getCommissionSummaryInfo;

  useEffect(() => {
    if (summaryData) {
      setSummary(summaryData);
    }
  }, [summaryData]);

  // * Helper: Get club names for display
  const getClubNames = (rowData) => {
    const clubs = [];

    // Get clubs from clubIds array first
    if (rowData.clubIds && rowData.clubIds.length > 0) {
      rowData.clubIds.forEach((club) => {
        if (typeof club === "object" && club.name) {
          clubs.push(club.name);
        } else if (typeof club === "string") {
          clubs.push(club);
        }
      });
    }
    // Fallback to single clubId
    else if (rowData.clubId) {
      if (typeof rowData.clubId === "object" && rowData.clubId.name) {
        clubs.push(rowData.clubId.name);
      }
    }

    if (clubs.length === 0) return "N/A";
    if (clubs.length === 1) return clubs[0];
    if (clubs.length <= 3) return clubs.join(", ");
    return `${clubs.slice(0, 2).join(", ")} +${clubs.length - 2} more`;
  };

  // * Helper: Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "bg-green-100 text-green-800";
      case "Rejected":
        return "bg-red-100 text-red-800";
      case "Pending":
      default:
        return "bg-yellow-100 text-yellow-800";
    }
  };

  // * Helper: Construct evidence URL
  const getEvidenceUrl = (evidencePath) => {
    if (!evidencePath) return null;
    // If it's already a full URL, return as is
    if (
      evidencePath.startsWith("http://") ||
      evidencePath.startsWith("https://")
    ) {
      return evidencePath;
    }

    // Ensure path uses forward slashes
    let cleanPath = evidencePath.replace(/\\/g, "/");

    // Ensure it starts with uploads/ if not already (though usually it will be)
    if (
      !cleanPath.startsWith("uploads/") &&
      !cleanPath.startsWith("/uploads/")
    ) {
      // If backend saves just filename or something else, we might need to prepend.
      // But based on controller logic: req.file.path.replace(/\\/g, '/'),
      // it saves full path including 'uploads/'.
      // So we mainly just need to ensure we don't STRIP it.
    }

    // Remove leading slash if present to avoid double slash when joining
    if (cleanPath.startsWith("/")) {
      cleanPath = cleanPath.substring(1);
    }
    // Construct full URL using the API base URL
    const apiBase = process.env.REACT_APP_API_URL || "";
    // If API base ends with /api, remove it for static file access
    const baseUrl = apiBase.replace(/\/api$/, "");
    return `${baseUrl}/${cleanPath}`;
  };

  // * Table Columns
  const getHeadCells = () => {
    const columns = [
      {
        field: "clubName",
        title: "Club(s)",
        render: (rowData) => (
          <span
            className="text-sm"
            title={
              rowData.clubIds?.map((c) => c.name || c).join(", ") ||
              rowData.clubId?.name ||
              "N/A"
            }
          >
            {getClubNames(rowData)}
          </span>
        ),
      },
      {
        field: "amount",
        title: "Amount",
        render: (rowData) => (
          <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm font-medium">
            ₹{(rowData.amount || 0).toLocaleString()}
          </span>
        ),
      },
      {
        field: "chipDistribution",
        title: "Chips",
        render: (rowData) => {
          // Check if chipDistribution exists and has values
          // MongoDB Map is serialized as an object, so check for object keys
          const chipData = rowData.chipDistribution;
          const hasChips =
            chipData &&
            ((chipData instanceof Map && chipData.size > 0) ||
              (typeof chipData === "object" &&
                Object.keys(chipData).length > 0));

          if (!hasChips) {
            return <span className="text-gray-400 italic text-xs">N/A</span>;
          }

          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setChipViewModal({ open: true, data: chipData });
              }}
              className="px-2 py-1 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50 transition-colors"
            >
              View Chips
            </button>
          );
        },
        cellStyle: { width: "100px" },
      },
      {
        field: "tableId",
        title: "Table Unique Number",
        render: (rowData) => {
          // Generate a truly unique identifier: ClubCode-TableNumber
          const clubCode =
            rowData.clubIds?.[0]?.clubCode || rowData.clubId?.clubCode || "";
          const tableNumber = rowData.tableId?.tableNumber || "";
          if (tableNumber && clubCode) {
            return (
              <span className="text-sm">
                {clubCode}-{tableNumber}
              </span>
            );
          } else if (tableNumber) {
            return <span className="text-sm">{tableNumber}</span>;
          }
          return <span className="text-sm">-</span>;
        },
      },
      {
        field: "dealerId",
        title: "Dealer",
        render: (rowData) => {
          // Try to get dealer name from dealerId first, fallback to identifier, then table's current dealer
          const dealerName =
            rowData.dealerId?.name ||
            rowData.dealerId?.identifier ||
            rowData.tableId?.currentSession?.dealer?.name ||
            rowData.tableId?.currentSession?.dealer?.identifier ||
            "-";
          return <span className="text-sm">{dealerName}</span>;
        },
      },
      {
        field: "evidenceStatus",
        title: "Evidence",
        render: (rowData) => (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span
                className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(rowData.evidenceStatus)}`}
              >
                {rowData.evidenceStatus || "Pending"}
              </span>
              {rowData.evidence && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEvidenceModal({
                      open: true,
                      url: getEvidenceUrl(rowData.evidence),
                      timestamp: rowData.evidenceTimestamp,
                      location: rowData.evidenceLocation,
                    });
                  }}
                  className="text-blue-500 hover:text-blue-700"
                  title="View Evidence"
                >
                  <Visibility style={{ fontSize: 18 }} />
                </button>
              )}
            </div>
            {rowData.evidenceTimestamp && (
              <span className="text-xs text-gray-500">
                {new Date(rowData.evidenceTimestamp).toLocaleString()}
              </span>
            )}
            {rowData.evidenceLocation?.latitude && (
              <span className="text-xs text-gray-500">
                {rowData.evidenceLocation.address || `${rowData.evidenceLocation.latitude.toFixed(4)}, ${rowData.evidenceLocation.longitude.toFixed(4)}`}
              </span>
            )}
          </div>
        ),
      },
      {
        field: "date",
        title: "Date",
        render: (rowData) => {
          if (rowData.date) {
            try {
              return new Date(rowData.date).toLocaleDateString();
            } catch (error) {
              return rowData.date;
            }
          }
          return "N/A";
        },
      },
      {
        field: "createdBy",
        title: "Created By",
        render: (rowData) => (
          <span className="text-sm">{rowData.createdBy?.name || "N/A"}</span>
        ),
      },
      {
        field: "timestamp",
        title: "Created At",
        render: (rowData) => {
          if (rowData.timestamp || rowData.createdAt) {
            try {
              const date = new Date(rowData.timestamp || rowData.createdAt);
              return (
                date.toLocaleDateString() + " " + date.toLocaleTimeString()
              );
            } catch (error) {
              return rowData.timestamp || rowData.createdAt;
            }
          }
          return "N/A";
        },
      },
      {
        title: "Actions",
        sorting: false,
        headerClassName: "action-column-header",
        bodyClassName: "action-column-cell",
        headerStyle: { textAlign: "center", width: 150, minWidth: 150 },
        render: (rowData) => (
          <div className="flex w-full items-center justify-center gap-2">
            {/* Approve/Reject buttons - only for users with rakeApproval permission */}
            {canApproveRake && rowData.evidenceStatus === "Pending" && (
              <>
                <button
                  onClick={() => openStatusModal(rowData, "Approved")}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 transition-all hover:-translate-y-0.5 hover:bg-emerald-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  title="Approve"
                  aria-label="Approve"
                >
                  <CheckCircle2 size={16} strokeWidth={2} />
                </button>
                <button
                  onClick={() => openStatusModal(rowData, "Rejected")}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200 transition-all hover:-translate-y-0.5 hover:bg-orange-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  title="Reject"
                  aria-label="Reject"
                >
                  <XCircle size={16} strokeWidth={2} />
                </button>
              </>
            )}
            {/* Delete button - only for Super Admin */}
            {canDeleteRake && (
              <button
                onClick={() => openDeleteModal(rowData)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200 transition-all hover:-translate-y-0.5 hover:bg-rose-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
                title="Delete Rake"
                aria-label="Delete Rake"
              >
                <Trash2 size={16} strokeWidth={2} />
              </button>
            )}
          </div>
        ),
        cellStyle: {
          textAlign: "center",
          width: 150,
          minWidth: 150,
        },
      },
    ];

    return columns;
  };

  // * Delete Single Commission
  const openDeleteModal = (commission) => {
    setSelectedCommissionForDelete(commission);
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setSelectedCommissionForDelete(null);
    setDeleteModal(false);
  };

  const handleDelete = () => {
    if (selectedCommissionForDelete) {
      dispatch(deleteDailyCommission(selectedCommissionForDelete._id));
    }
  };

  // * Bulk Delete
  const openBulkDeleteModal = () => {
    if (selectedCommissionsForBulkDelete.length === 0) {
      toast("Please select Rake records to delete", {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
      return;
    }
    setBulkDeleteModal(true);
  };

  const closeBulkDeleteModal = () => {
    setBulkDeleteModal(false);
  };

  const handleBulkDelete = () => {
    selectedCommissionsForBulkDelete.forEach((commission) => {
      dispatch(deleteDailyCommission(commission._id));
    });
  };

  // * Status Modal
  const openStatusModal = (commission, status) => {
    setSelectedCommissionForStatus(commission);
    setNewStatus(status);
    setStatusRemarks("");
    setStatusModal(true);
  };

  const closeStatusModal = () => {
    setSelectedCommissionForStatus(null);
    setNewStatus("");
    setStatusRemarks("");
    setStatusModal(false);
  };

  const handleStatusUpdate = () => {
    if (selectedCommissionForStatus && newStatus) {
      dispatch(
        updateEvidenceStatus(
          selectedCommissionForStatus._id,
          newStatus,
          statusRemarks,
        ),
      );
    }
  };

  // * Evidence status update handler
  const updateEvidenceStatusInfo = useSelector(
    (state) => state.updateEvidenceStatus,
  );
  const {
    loading: loadingStatusUpdate,
    success: statusUpdateSuccess,
    error: statusUpdateError,
  } = updateEvidenceStatusInfo;

  const bulkUpdateEvidenceStatusInfo = useSelector(
    (state) => state.bulkUpdateEvidenceStatus,
  );
  const {
    loading: loadingBulkStatus,
    success: bulkStatusSuccess,
    error: bulkStatusError,
  } = bulkUpdateEvidenceStatusInfo;

  const exportDailyCommissionsInfo = useSelector(
    (state) => state.exportDailyCommissions,
  );
  const { loading: loadingExport, error: exportError } =
    exportDailyCommissionsInfo;

  useEffect(() => {
    if (statusUpdateSuccess) {
      toast("Evidence status updated successfully", {
        type: "success",
        hideProgressBar: true,
        autoClose: 2000,
      });
      closeStatusModal();
      dispatch({ type: UPDATE_EVIDENCE_STATUS_RESET });
      dispatch(getDailyCommissions({}));
      dispatch(getCommissionSummary({}));
    } else if (statusUpdateError) {
      toast(statusUpdateError, {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
      dispatch({ type: UPDATE_EVIDENCE_STATUS_RESET });
    }
  }, [statusUpdateSuccess, statusUpdateError, dispatch]);

  // * Bulk Status Update Effect
  useEffect(() => {
    if (bulkStatusSuccess) {
      toast("Bulk status updated successfully", {
        type: "success",
        hideProgressBar: true,
        autoClose: 2000,
      });
      closeBulkApproveModal();
      dispatch({ type: BULK_UPDATE_EVIDENCE_STATUS_RESET });
      setSelectedCommissionsForBulkDelete([]); // Reset selection
      dispatch(getDailyCommissions({}));
      dispatch(getCommissionSummary({}));
    } else if (bulkStatusError) {
      toast(bulkStatusError, {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
      dispatch({ type: BULK_UPDATE_EVIDENCE_STATUS_RESET });
    }
  }, [bulkStatusSuccess, bulkStatusError, dispatch]);

  // * Date Preset Handler
  const handleDatePreset = (preset) => {
    setDatePreset(preset);
    const today = new Date();
    let start = "";
    let end = "";

    if (preset === "today") {
      start = today.toISOString().split("T")[0];
      end = today.toISOString().split("T")[0];
    } else if (preset === "7days") {
      const pastDate = new Date();
      pastDate.setDate(today.getDate() - 6);
      start = pastDate.toISOString().split("T")[0];
      end = today.toISOString().split("T")[0];
    } else {
      // Custom means we don't force dates, user selects
      // But if switching FROM preset to custom, maybe keep current?
      // For now, just set preset state.
    }

    if (start && end) {
      setStartDate(start);
      setEndDate(end);
    }
  };

  // * Export Handlers
  const prepareExportData = (dataToExport) => {
    return dataToExport.map((item) => {
      // Club Name(s)
      const clubs = [];
      if (item.clubIds && item.clubIds.length > 0) {
        item.clubIds.forEach((c) =>
          clubs.push(typeof c === "object" ? c.name : c),
        );
      } else if (item.clubId) {
        clubs.push(
          typeof item.clubId === "object" ? item.clubId.name : item.clubId,
        );
      }
      const clubStr = clubs.join(", ") || "N/A";

      // Table ID
      let tableStr = "-";
      const clubCode =
        item.clubIds?.[0]?.clubCode || item.clubId?.clubCode || "";
      const tableNumber = item.tableId?.tableNumber || "";
      if (tableNumber && clubCode) tableStr = `${clubCode}-${tableNumber}`;
      else if (tableNumber) tableStr = tableNumber;

      // Dealer
      const dealerName =
        item.dealerId?.name || item.dealerId?.identifier || "-";

      return {
        Date: item.date ? new Date(item.date).toLocaleDateString() : "N/A",
        Club: clubStr,
        Amount: item.amount || 0,
        Table: tableStr,
        Dealer: dealerName,
        Status: item.evidenceStatus || "Pending",
        "Created By": item.createdBy?.name || "N/A",
        "Created At": item.createdAt
          ? new Date(item.createdAt).toLocaleString()
          : "N/A",
      };
    });
  };

  const handleExportExcel = () => {
    const exportData = prepareExportData(data);
    const columns = [
      { title: "Date", field: "Date" },
      { title: "Club", field: "Club" },
      { title: "Amount", field: "Amount" },
      { title: "Table", field: "Table" },
      { title: "Dealer", field: "Dealer" },
      { title: "Status", field: "Status" },
      { title: "Created By", field: "Created By" },
    ];
    exportToExcel(exportData, columns, "Daily_Commission_Report");
  };

  const handleExportPDF = () => {
    const exportData = prepareExportData(data);
    const columns = [
      { title: "Date", field: "Date" },
      { title: "Club", field: "Club" },
      { title: "Amount", field: "Amount" },
      { title: "Table", field: "Table" },
      { title: "Dealer", field: "Dealer" },
      { title: "Status", field: "Status" },
    ];
    exportToPDF(
      exportData,
      columns,
      "Daily_Commission_Report",
      "Daily Commission Report",
    );
  };

  // * Bulk Approve Handlers
  const openBulkApproveModal = () => {
    if (selectedCommissionsForBulkDelete.length === 0) {
      toast("Please select at least one record", {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
      return;
    }
    setBulkApproveModal(true);
  };

  const closeBulkApproveModal = () => {
    setBulkApproveModal(false);
  };

  const handleBulkApprove = () => {
    const ids = selectedCommissionsForBulkDelete.map((c) => c._id);
    dispatch(
      bulkUpdateEvidenceStatus(
        ids,
        "Approved",
        "Bulk Approved via Admin Panel",
      ),
    );
  };

  // * Open Add Commission Modal
  const openAddCommissionModal = () => {
    setOpenModal(true);
    setDate(new Date().toISOString().split("T")[0]); // Set today's date as default
    setAmount("");
    setSelectedClubIds([]);
    // setRakeType('percentage')
    // setRakeValue('')
    setRemarks("");
    setEvidenceFile(null);
    setEvidencePreview(null);
    setActiveTables([]);
    setTableRakeValues({});
    setAvailableDealers([]);
    setSelectedDealerId("");
  };

  // * Close Add Commission Modal
  const closeAddCommissionModal = () => {
    setOpenModal(false);
    setAmount("");
    setDate("");
    setSelectedClubIds([]);
    // setRakeType('percentage')
    // setRakeValue('')
    setRemarks("");
    setEvidenceFile(null);
    setEvidencePreview(null);
    setEvidenceTimestamp(null);
    setEvidenceLocationData(null);
    setActiveTables([]);
    setTableRakeValues({});
    setSelectedTableId("");
    setAvailableDealers([]);
    setSelectedDealerId("");
    // Reset chip distribution states
    setChipDistribution({});
    setCalculatedChips({});
    setShowChipDistribution(false);
    // Stop camera if open
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  // * Capture geolocation and timestamp when evidence is taken
  const captureLocationAndTimestamp = () => {
    setEvidenceTimestamp(new Date().toISOString());
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setEvidenceLocationData({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          console.warn("Geolocation error:", error.message);
          setEvidenceLocationData(null);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    }
  };

  // * Handle Evidence File Selection
  const handleEvidenceChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast("Only image files (jpg, png, gif, webp) are allowed", {
          type: "error",
          hideProgressBar: true,
          autoClose: 2000,
        });
        return;
      }
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast("File size must be less than 5MB", {
          type: "error",
          hideProgressBar: true,
          autoClose: 2000,
        });
        return;
      }
      setEvidenceFile(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setEvidencePreview(previewUrl);
      captureLocationAndTimestamp();
    }
  };

  // * Camera Capture Functions
  const startCamera = async () => {
    // Check if mediaDevices is available (requires HTTPS or localhost)
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast("Camera not available. Please use HTTPS or access via localhost.", {
        type: "error",
        hideProgressBar: true,
        autoClose: 4000,
      });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }, // Use back camera on mobile
      });
      setCameraStream(stream);
      setShowCamera(true);
      // Attach stream to video element after state update
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (error) {
      console.error("Error accessing camera:", error);
      let errorMsg = "Unable to access camera.";
      if (error.name === "NotAllowedError") {
        errorMsg = "Camera permission denied. Please allow camera access.";
      } else if (error.name === "NotFoundError") {
        errorMsg = "No camera found on this device.";
      } else if (error.name === "NotReadableError") {
        errorMsg = "Camera is in use by another app.";
      } else if (
        window.location.protocol !== "https:" &&
        window.location.hostname !== "localhost" &&
        window.location.hostname !== "127.0.0.1"
      ) {
        errorMsg =
          "Camera requires HTTPS. Access via localhost or enable HTTPS.";
      }
      toast(errorMsg, {
        type: "error",
        hideProgressBar: true,
        autoClose: 4000,
      });
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0);

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            // Create a file from the blob
            const file = new File([blob], `evidence_${Date.now()}.jpg`, {
              type: "image/jpeg",
            });
            setEvidenceFile(file);
            const previewUrl = URL.createObjectURL(blob);
            setEvidencePreview(previewUrl);
            captureLocationAndTimestamp();
            stopCamera();
          }
        },
        "image/jpeg",
        0.85,
      );
    }
  };

  // * Toggle club selection
  const toggleClubSelection = (clubId) => {
    setSelectedClubIds((prev) => {
      if (prev.includes(clubId)) {
        return prev.filter((id) => id !== clubId);
      } else {
        return [...prev, clubId];
      }
    });
  };

  // * Toggle filter club selection
  const toggleFilterClubSelection = (clubId) => {
    setFilterClubIds((prev) => {
      if (prev.includes(clubId)) {
        return prev.filter((id) => id !== clubId);
      } else {
        return [...prev, clubId];
      }
    });
  };

  // * Add Commission
  const addCommissionHandler = () => {
    if (!amount || !date) {
      toast("Amount and Date are mandatory", {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
      return;
    }

    if (selectedClubIds.length === 0) {
      toast("Please select at least one club", {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
      return;
    }

    // Validate table selection
    if (activeTables.length > 0 && !selectedTableId) {
      toast("Please select a table", {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
      return;
    }

    // Construct Remarks from Table Breakdown if available
    let finalRemarks = remarks || "";

    // Get tables with rake values entered
    const tablesWithValues = activeTables.filter(
      (t) => tableRakeValues[t._id] && parseFloat(tableRakeValues[t._id]) > 0,
    );

    if (tablesWithValues.length > 0) {
      const breakdown = tablesWithValues
        .map(
          (t) => `${t.tableName || t.tableNumber}: ₹${tableRakeValues[t._id]}`,
        )
        .join(", ");

      if (breakdown) {
        finalRemarks = finalRemarks
          ? `${finalRemarks}\n\n[Table Breakdown]: ${breakdown}`
          : `[Table Breakdown]: ${breakdown}`;
      }
    }

    // ALWAYS use FormData to ensure compatibility with backend Multer middleware
    const formData = new FormData();
    formData.append("amount", parseFloat(amount));
    formData.append("date", date);

    // Append clubIds array properly
    selectedClubIds.forEach((id) => formData.append("clubIds[]", id));

    // Provide a fallback for legacy single clubId if needed by backend validation (though backend should handle clubIds)
    if (selectedClubIds.length > 0) {
      formData.append("clubId", selectedClubIds[0]);
    }

    formData.append("rakeType", "fixed"); // Default logic

    // Send tableId - prioritize dropdown selection, fallback to first table with value
    let tableToUse = null;
    if (selectedTableId) {
      formData.append("tableId", selectedTableId);
      tableToUse = activeTables.find((t) => t._id === selectedTableId);
    } else if (tablesWithValues.length > 0) {
      formData.append("tableId", tablesWithValues[0]._id);
      tableToUse = tablesWithValues[0];
    }

    // Set dealerId - prioritize manually selected dealer, fallback to table's current session dealer
    if (selectedDealerId) {
      formData.append("dealerId", selectedDealerId);
      // Find the selected dealer to get their source (user or admin)
      const selectedDealer = availableDealers.find(
        (d) => d._id === selectedDealerId,
      );
      if (selectedDealer?.source) {
        // Convert source to model name (user -> User, admin -> Admin)
        const dealerModel =
          selectedDealer.source === "admin" ? "Admin" : "User";
        formData.append("dealerModel", dealerModel);
      }
    } else if (tableToUse?.currentSession?.dealer) {
      const dealerId =
        typeof tableToUse.currentSession.dealer === "object"
          ? tableToUse.currentSession.dealer._id
          : tableToUse.currentSession.dealer;
      if (dealerId) {
        formData.append("dealerId", dealerId);
        formData.append("dealerModel", "User"); // Table dealers are typically from User model
      }
    }

    if (finalRemarks) {
      formData.append("remarks", finalRemarks);
    }

    if (evidenceFile) {
      formData.append("evidence", evidenceFile);
      if (evidenceTimestamp) {
        formData.append("evidenceTimestamp", evidenceTimestamp);
      }
      if (evidenceLocationData) {
        formData.append("evidenceLatitude", evidenceLocationData.latitude);
        formData.append("evidenceLongitude", evidenceLocationData.longitude);
        if (evidenceLocationData.accuracy) {
          formData.append("evidenceAccuracy", evidenceLocationData.accuracy);
        }
      }
    }

    // MANDATORY: Chip distribution must match amount
    const chipTotal = calculateTotalFromChips();
    if (chipTotal !== parseFloat(amount)) {
      toast(
        `Chip distribution total (₹${chipTotal.toLocaleString()}) doesn't match amount (₹${parseFloat(amount).toLocaleString()})`,
        {
          type: "error",
          hideProgressBar: true,
          autoClose: 4000,
        },
      );
      return;
    }

    if (Object.keys(chipDistribution).length > 0) {
      const chipDistributionJSON = JSON.stringify(chipDistribution);
      formData.append("chipDistribution", chipDistributionJSON);
    } else {
      // Should not happen if amount > 0 and validation passes, but good to be safe
      toast("Please enter chip distribution", {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
      return;
    }

    dispatch(addDailyCommission(formData));
  };

  const addCommissionInfo = useSelector((state) => state.addDailyCommission);
  const {
    loading: loadingAddCommission,
    error: errorAddCommission,
    success: addCommissionSuccess,
  } = addCommissionInfo;

  useEffect(() => {
    if (addCommissionSuccess) {
      toast("Rake record added successfully", {
        type: "success",
        hideProgressBar: true,
        autoClose: 2000,
      });
      closeAddCommissionModal();
      dispatch({ type: ADD_DAILY_COMMISSION_RESET });
      dispatch(getDailyCommissions({}));
      dispatch(getCommissionSummary({}));
    } else if (errorAddCommission) {
      toast(errorAddCommission, {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
      dispatch({ type: ADD_DAILY_COMMISSION_RESET });
    }
  }, [addCommissionSuccess, errorAddCommission, dispatch]);

  // * Delete Commission Handlers
  const deleteCommissionInfo = useSelector(
    (state) => state.deleteDailyCommission,
  );
  const {
    loading: loadingDeleteCommission,
    error: errorDeleteCommission,
    success: deleteCommissionSuccess,
  } = deleteCommissionInfo;

  useEffect(() => {
    if (deleteCommissionSuccess) {
      toast("Rake record deleted successfully", {
        type: "success",
        hideProgressBar: true,
        autoClose: 2000,
      });
      closeDeleteModal();
      closeBulkDeleteModal();
      setSelectedCommissionsForBulkDelete([]);
      dispatch({ type: DELETE_DAILY_COMMISSION_RESET });
      dispatch(getDailyCommissions({}));
      dispatch(getCommissionSummary({}));
    } else if (errorDeleteCommission) {
      toast(errorDeleteCommission, {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
      dispatch({ type: DELETE_DAILY_COMMISSION_RESET });
    }
  }, [deleteCommissionSuccess, errorDeleteCommission, dispatch]);

  // * Apply Filters
  const applyFilters = () => {
    const filters = {
      startDate,
      endDate,
      clubIds: filterClubIds.length > 0 ? filterClubIds : undefined,
      evidenceStatus: filterEvidenceStatus || undefined,
      rakeType: filterRakeType || undefined,
      isActive: filterIsActive !== "" ? filterIsActive : undefined,
    };
    dispatch(getDailyCommissions(filters));
    dispatch(getCommissionSummary(filters));
  };

  // * Clear Filters
  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setFilterClubIds([]);
    setFilterEvidenceStatus("");
    setFilterRakeType("");
    setFilterIsActive("");
    dispatch(getDailyCommissions({}));
    dispatch(getCommissionSummary({}));
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="w-full h-full">
      <ScreenHeader title="Daily rake" />

      {/* Summary Cards */}
      <div className="bg-white shadow-md rounded px-4 sm:px-8 py-3 sm:py-4 my-3 sm:my-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-3 sm:mb-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-blue-800">Total Amount</h3>
            <p className="text-2xl font-bold text-blue-900">
              ₹{(summary.totalAmount || 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-green-800">
              Total Records
            </h3>
            <p className="text-2xl font-bold text-green-900">
              {summary.totalCount || 0}
            </p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-purple-800">
              Average Amount
            </h3>
            <p className="text-2xl font-bold text-purple-900">
              ₹{(summary.avgAmount || 0).toLocaleString()}
            </p>
          </div>
          {/* Status Counts */}
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-yellow-800">Pending</h3>
            <p className="text-2xl font-bold text-yellow-900">
              {summary.statusCounts?.pending || 0}
            </p>
          </div>
          <div className="bg-emerald-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-emerald-800">Approved</h3>
            <p className="text-2xl font-bold text-emerald-900">
              {summary.statusCounts?.approved || 0}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
            {canDeleteRake && (
              <button
                type="button"
                onClick={openBulkDeleteModal}
                title="Delete Selected"
                aria-label="Delete Selected"
                className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-red-100 text-red-600 transition-colors hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-400"
              >
                <Trash2 size={18} strokeWidth={2.2} />
              </button>
            )}
            {canApproveRake && (
              <button
                type="button"
                onClick={openBulkApproveModal}
                title="Approve Selected"
                aria-label="Approve Selected"
                className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-emerald-100 text-emerald-600 transition-colors hover:bg-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              >
                <CheckCheck size={18} strokeWidth={2.2} />
              </button>
            )}
            <button type="button" onClick={handleExportExcel} title="Export to Excel" aria-label="Export to Excel" className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-inset ring-teal-200 transition-all hover:bg-teal-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-cm-gold">
              <FileSpreadsheet size={18} strokeWidth={2} />
            </button>
            <button type="button" onClick={handleExportPDF} title="Export to PDF" aria-label="Export to PDF" className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200 transition-all hover:bg-violet-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400">
              <FileDown size={18} strokeWidth={2} />
            </button>
            <FilterToggle
              open={showFilters}
              onToggle={() => setShowFilters(!showFilters)}
              count={
                (startDate || endDate ? 1 : 0) +
                (filterEvidenceStatus ? 1 : 0) +
                (filterRakeType ? 1 : 0) +
                (filterClubIds.length ? 1 : 0)
              }
              controls="rake-filters"
            />
          {canAddRake && (
              <button
                type="button"
                onClick={openAddCommissionModal}
                title="Add Rake"
                aria-label="Add Rake"
                className="ml-auto inline-flex h-10 w-10 items-center justify-center rounded-md bg-emerald-100 text-emerald-600 transition-colors hover:bg-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              >
                <Plus size={19} strokeWidth={2.2} />
              </button>
          )}
        </div>

        {/* Filters */}
        <FilterPanel open={showFilters} id="rake-filters">
          <div className="mt-4 p-4 bg-gray-50 rounded-lg cm-filter-stagger">
            {/* Quick Date Presets */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => handleDatePreset("today")}
                className={`px-3 py-1 text-xs rounded border ${datePreset === "today" ? "bg-blue-100 border-blue-500 text-blue-700" : "bg-white border-gray-300"}`}
              >
                Today
              </button>
              <button
                onClick={() => handleDatePreset("7days")}
                className={`px-3 py-1 text-xs rounded border ${datePreset === "7days" ? "bg-blue-100 border-blue-500 text-blue-700" : "bg-white border-gray-300"}`}
              >
                Last 7 Days
              </button>
              <button
                onClick={() => handleDatePreset("custom")}
                className={`px-3 py-1 text-xs rounded border ${datePreset === "custom" ? "bg-blue-100 border-blue-500 text-blue-700" : "bg-white border-gray-300"}`}
              >
                Custom Range
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              {/* Date Range */}
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              {/* Evidence Status */}
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Evidence Status
                </label>
                <select
                  value={filterEvidenceStatus}
                  onChange={(e) => setFilterEvidenceStatus(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
              {/* Rake Type */}
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Rake Type
                </label>
                <select
                  value={filterRakeType}
                  onChange={(e) => setFilterRakeType(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">All Types</option>
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed</option>
                </select>
              </div>
            </div>

            {/* Club Multi-Select Filter */}
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700">
                Filter by Clubs
              </label>
              <div className="mt-2 border border-gray-300 rounded-md p-2 max-h-40 overflow-y-auto bg-white">
                {loadingClubs ? (
                  <p className="text-sm text-gray-500">Loading clubs...</p>
                ) : clubsError ? (
                  <p className="text-sm text-red-500">{clubsError}</p>
                ) : availableClubs.length > 0 ? (
                  availableClubs.map((club) => (
                    <label
                      key={club._id}
                      className="flex items-center gap-2 py-1 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={filterClubIds.includes(club._id)}
                        onChange={() => toggleFilterClubSelection(club._id)}
                        className="rounded"
                      />
                      <span className="text-sm">
                        {club.name} ({club.clubCode})
                      </span>
                    </label>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">
                    No assigned clubs. Contact admin.
                  </p>
                )}
              </div>
              {filterClubIds.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {filterClubIds.length} club(s) selected
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                onClick={applyFilters}
                text="Apply Filters"
                className="bg-green-500 hover:bg-green-700"
              />
              <Button
                type="button"
                onClick={clearFilters}
                text="Clear Filters"
                className="bg-gray-500 hover:bg-gray-700"
              />
            </div>
          </div>
        </FilterPanel>
      </div>

      <MaterialTable
        icons={tableIcons}
        title="Daily Rake Records"
        columns={getHeadCells()}
        data={data}
        options={{
          exportButton: false,
          search: true,
          exportAllData: false,
          rowStyle: {
            height: "5px",
            fontSize: 13,
          },
          paging: true,
          pageSize: 10,
          emptyRowsWhenPaging: false,
          pageSizeOptions: [10, 20, 50],
          headerStyle: {
            position: "sticky",
            top: "0",
          },
          selection: canDeleteRake,
        }}
        onSearchChange={(searchText) => {
          const filters = {
            search: searchText || "",
            startDate,
            endDate,
            clubIds: filterClubIds.length > 0 ? filterClubIds : undefined,
            evidenceStatus: filterEvidenceStatus || undefined,
          };
          dispatch(getDailyCommissions(filters));
        }}
        onSelectionChange={(rows) => setSelectedCommissionsForBulkDelete(rows)}
      />

      {/* Add Commission Modal */}
      {openModal && (
        <>
          {/* Modal Overlay */}
          <div className="fixed inset-0 z-40 bg-black opacity-25"></div>

          {/* Modal Container */}
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto min-h-screen">
            <div className="relative w-full max-w-lg mx-auto bg-white rounded-lg shadow-lg my-8">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
                <h3 className="text-lg font-semibold">Add Daily Rake</h3>
              </div>

              {/* Modal Body */}
              <div className="px-4 py-4 max-h-[70vh] overflow-y-auto">
                {/* Date - Moved to TOP as per requirement */}
                <div className="w-full mb-4">
                  <label className="text-sm font-medium text-gray-700">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>

                {/* Club Multi-Select */}
                <div className="w-full mb-4">
                  <label className="text-sm font-medium text-gray-700">
                    Select Club(s) *
                  </label>
                  <div className="mt-2 border border-gray-300 rounded-md p-2 max-h-40 overflow-y-auto">
                    {loadingClubs ? (
                      <p className="text-sm text-gray-500">Loading clubs...</p>
                    ) : clubsError ? (
                      <p className="text-sm text-red-500">{clubsError}</p>
                    ) : availableClubs.length > 0 ? (
                      availableClubs.map((club) => (
                        <label
                          key={club._id}
                          className="flex items-center gap-2 py-1 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedClubIds.includes(club._id)}
                            onChange={() => toggleClubSelection(club._id)}
                            className="rounded"
                          />
                          <span className="text-sm">
                            {club.name} ({club.clubCode})
                          </span>
                        </label>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">
                        No assigned clubs. Contact admin.
                      </p>
                    )}
                  </div>
                  {selectedClubIds.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedClubIds.length} club(s) selected
                    </p>
                  )}
                </div>

                {/* Table Selection Dropdown */}
                {selectedClubIds.length > 0 && (
                  <div className="w-full mb-4">
                    <label className="text-sm font-medium text-gray-700">
                      Select Table *
                    </label>
                    {loadingTables ? (
                      <div className="text-sm text-gray-500 py-2">
                        Loading tables...
                      </div>
                    ) : (
                      <select
                        value={selectedTableId}
                        onChange={(e) => setSelectedTableId(e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      >
                        <option value="">-- Select a Table --</option>
                        {activeTables.map((table) => {
                          const dealerName =
                            table.currentSession?.dealer?.name ||
                            table.currentSession?.dealer?.identifier ||
                            "";
                          return (
                            <option key={table._id} value={table._id}>
                              {table.tableNumber} - {table.tableName}
                              {dealerName ? ` (Dealer: ${dealerName})` : ""}
                            </option>
                          );
                        })}
                      </select>
                    )}
                    {activeTables.length === 0 && !loadingTables && (
                      <p className="text-xs text-red-500 mt-1">
                        No active tables found for selected club(s)
                      </p>
                    )}
                  </div>
                )}

                {/* Coordinator Selection Dropdown */}
                {selectedClubIds.length > 0 && (
                  <div className="w-full mb-4">
                    <label className="text-sm font-medium text-gray-700">
                      Select Coordinator *
                    </label>
                    {loadingDealers ? (
                      <div className="text-sm text-gray-500 py-2">
                        Loading staff...
                      </div>
                    ) : (
                      <select
                        value={selectedDealerId}
                        onChange={(e) => setSelectedDealerId(e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      >
                        <option value="">-- Select a Coordinator --</option>
                        {availableDealers.map((staff) => (
                          <option key={staff._id} value={staff._id}>
                            {staff.name || staff.identifier || "Unknown"}{" "}
                            {staff.roleName ? `(${staff.roleName})` : ""}
                          </option>
                        ))}
                      </select>
                    )}
                    {availableDealers.length === 0 && !loadingDealers && (
                      <p className="text-xs text-orange-500 mt-1">
                        No staff found for selected club(s). You can still add
                        rake without a coordinator.
                      </p>
                    )}
                  </div>
                )}

                {/* Table Rake Breakdown - Optional Section */}
                {selectedClubIds.length > 0 && activeTables.length > 0 && (
                  <div className="w-full mb-4 bg-gray-50 p-3 rounded-md border border-gray-200">
                    <label className="text-sm font-semibold text-gray-700 block mb-2">
                      Table Values (Optional Breakdown)
                    </label>
                    <div className="space-y-2">
                      {activeTables.map((table) => (
                        <div
                          key={table._id}
                          className="flex items-center justify-between"
                        >
                          <span
                            className="text-sm text-gray-600 flex-1 truncate pr-2"
                            title={table.tableName}
                          >
                            {table.tableNumber} - {table.tableName}
                          </span>
                          <div className="flex items-center">
                            <span className="text-gray-500 text-sm mr-1">
                              ₹
                            </span>
                            <input
                              type="number"
                              placeholder="0"
                              value={tableRakeValues[table._id] || ""}
                              onChange={(e) =>
                                handleTableRakeChange(table._id, e.target.value)
                              }
                              className="w-24 border border-gray-300 rounded px-2 py-1 text-sm text-right focus:outline-none focus:border-blue-500"
                              min="0"
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Total Amount Auto-Calculated */}
                    <div className="mt-3 pt-2 border-t border-gray-300 flex justify-between items-center">
                      <span className="font-semibold text-sm">
                        Total Amount:
                      </span>
                      <span className="font-bold text-lg text-emerald-600">
                        ₹ {(amount || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}

                {/* Amount (Read Only / Hidden if using table calc, but kept visible for verification or manual override if tables fail) */}
                {/* User requested "Then amount when i check club tables must appear".
                    We make it read-only if tables are present, or editable?
                    Let's keep it editable but typically populated by the breakdown.
                 */}
                <div className="w-full mb-4">
                  <label className="text-sm font-medium text-gray-700">
                    Total Amount *
                  </label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => {
                      const newAmount = e.target.value;
                      setAmount(newAmount);
                      // Auto-calculate chip distribution when amount changes
                      if (newAmount && parseFloat(newAmount) > 0) {
                        const calculated = calculateChipDistributionHelper(
                          parseFloat(newAmount),
                        );
                        setCalculatedChips(calculated);
                        setChipDistribution(calculated);
                      } else {
                        setCalculatedChips({});
                        setChipDistribution({});
                      }
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-100"
                    placeholder="Total calculated from tables"
                    // readOnly={activeTables.length > 0} // Optional: make read only
                  />
                </div>

                {/* Rake Type REMOVED */}

                {/* Rake Value REMOVED */}

                {/* ===== CHIP DISTRIBUTION SECTION ===== */}
                <div className="w-full mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      Chip Distribution (Mandatory)
                    </label>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={resetChipsToCalculated}
                        className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                      >
                        Auto Calculate
                      </button>
                      <span className="text-xs text-gray-500">
                        Total: ₹{calculateTotalFromChips().toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {/* Chip Distribution Panel - Always Visible */}
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-medium text-gray-700">
                        Enter chip counts by denomination
                      </span>
                    </div>

                    {/* Chip Input Grid */}
                    <div className="grid grid-cols-6 gap-2">
                      {[25000, 10000, 5000, 1000, 500, 100].map(
                        (denomination) => (
                          <div
                            key={denomination}
                            className="flex flex-col space-y-1"
                          >
                            <label className="text-xs font-medium text-center text-gray-600">
                              ₹{denomination.toLocaleString()}
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={chipDistribution[denomination] || ""}
                              onChange={(e) =>
                                handleChipChange(denomination, e.target.value)
                              }
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-500 text-center"
                              placeholder="0"
                            />
                            <span className="text-xs text-gray-400 text-center">
                              chips
                            </span>
                          </div>
                        ),
                      )}
                    </div>

                    {/* Validation Message */}
                    {amount &&
                      calculateTotalFromChips() > 0 &&
                      calculateTotalFromChips() !== parseFloat(amount) && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
                          ⚠️ Chip total (₹
                          {calculateTotalFromChips().toLocaleString()}) doesn't
                          match amount (₹{parseFloat(amount).toLocaleString()})
                        </div>
                      )}

                    {/* Success indicator */}
                    {amount &&
                      calculateTotalFromChips() > 0 &&
                      calculateTotalFromChips() === parseFloat(amount) && (
                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-600">
                          ✅ Chip distribution matches the amount
                        </div>
                      )}
                  </div>
                </div>

                {/* Remarks */}
                <div className="w-full mb-4">
                  <label className="text-sm font-medium text-gray-700">
                    Remarks
                  </label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    rows="2"
                    placeholder="Optional remarks..."
                  />
                </div>

                {/* Evidence Upload */}
                <div className="w-full mb-4">
                  <label className="text-sm font-medium text-gray-700">
                    Evidence (Image)
                  </label>
                  <div className="mt-1 flex flex-col gap-2">
                    {/* File Input and Camera Button Row */}
                    <div className="flex gap-2 items-center flex-wrap">
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={handleEvidenceChange}
                        className="flex-1 min-w-0 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                      />
                      {!showCamera && (
                        <button
                          type="button"
                          onClick={startCamera}
                          className="px-3 py-2 bg-blue-500 text-white rounded text-sm font-medium hover:bg-blue-600 flex items-center gap-1"
                        >
                          📷 Capture
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">
                      Accepted: JPG, PNG, GIF, WebP (Max 5MB) or use camera to
                      capture
                    </p>

                    {/* Camera Preview */}
                    {showCamera && (
                      <div className="relative mt-2 bg-black rounded-lg overflow-hidden">
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          className="w-full max-h-60 object-contain"
                        />
                        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2">
                          <button
                            type="button"
                            onClick={capturePhoto}
                            className="px-4 py-2 bg-cm-gold text-cm-gold-ink rounded-full text-sm font-medium hover:bg-cm-gold"
                          >
                            📸 Take Photo
                          </button>
                          <button
                            type="button"
                            onClick={stopCamera}
                            className="px-4 py-2 bg-red-500 text-white rounded-full text-sm font-medium hover:bg-red-600"
                          >
                            ✕ Cancel
                          </button>
                        </div>
                        <canvas ref={canvasRef} className="hidden" />
                      </div>
                    )}

                    {/* Evidence Preview */}
                    {evidencePreview && !showCamera && (
                      <div className="relative mt-2">
                        <img
                          src={evidencePreview}
                          alt="Evidence preview"
                          className="max-h-40 rounded border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setEvidenceFile(null);
                            setEvidencePreview(null);
                          }}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Note */}
                <p className="text-xs text-gray-500 mt-2">
                  All the fields with * are mandatory
                </p>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end px-4 py-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeAddCommissionModal}
                  className="text-red-500 font-bold uppercase text-sm px-4 py-2 mr-2"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={addCommissionHandler}
                  disabled={loadingAddCommission}
                  className={`${
                    loadingAddCommission
                      ? "bg-gray-300"
                      : "bg-emerald-500 hover:bg-emerald-600"
                  } text-white font-bold uppercase text-sm px-6 py-2 rounded shadow`}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-auto my-6 mx-auto max-w-3xl">
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                <div className="flex items-center justify-between py-1 px-3 border-b border-solid border-blueGray-200 rounded-t">
                  <h3 className="text-lg font-semibold mt-0 mb-0">
                    Delete Rake
                  </h3>
                </div>
                <div className="relative p-6 flex-auto">
                  <p className="text-gray-500 text-sm leading-relaxed">
                    Are you sure you want to delete this Rake record of{" "}
                    <strong>₹{selectedCommissionForDelete?.amount}</strong>?
                  </p>
                  <p className="text-gray-400 text-xs mt-2">
                    This action cannot be undone.
                  </p>
                </div>
                <div className="flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b">
                  <button
                    className="text-gray-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    type="button"
                    onClick={closeDeleteModal}
                  >
                    Cancel
                  </button>
                  <button
                    className="bg-red-500 text-white active:bg-red-600 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300"
                    type="button"
                    onClick={handleDelete}
                    disabled={loadingDeleteCommission}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      )}

      {/* Bulk Delete Modal */}
      {bulkDeleteModal && (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-auto my-6 mx-auto max-w-md">
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                <div className="flex items-center justify-between py-1 px-3 border-b border-solid border-blueGray-200 rounded-t">
                  <h3 className="text-lg font-semibold mt-0 mb-0">
                    Delete Multiple Rake
                  </h3>
                </div>
                <div className="relative p-6 flex-auto">
                  <p className="text-gray-500 text-sm leading-relaxed mb-3">
                    Are you sure you want to delete{" "}
                    <strong>{selectedCommissionsForBulkDelete.length}</strong>{" "}
                    selected rake records?
                  </p>
                  <div className="max-h-32 overflow-y-auto">
                    {selectedCommissionsForBulkDelete.map(
                      (commission, index) => (
                        <p key={index} className="text-xs text-gray-600">
                          • ₹{commission.amount} -{" "}
                          {new Date(commission.date).toLocaleDateString()}
                        </p>
                      ),
                    )}
                  </div>
                  <p className="text-gray-400 text-xs mt-2">
                    This action cannot be undone.
                  </p>
                </div>
                <div className="flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b">
                  <button
                    className="text-gray-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    type="button"
                    onClick={closeBulkDeleteModal}
                  >
                    Cancel
                  </button>
                  <button
                    className="bg-red-500 text-white active:bg-red-600 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300"
                    type="button"
                    onClick={handleBulkDelete}
                    disabled={loadingDeleteCommission}
                  >
                    Delete All
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      )}

      {/* Evidence Preview Modal */}
      {evidenceModal.open && (
        <>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75"
            onClick={() => setEvidenceModal({ open: false, url: null, timestamp: null, location: null })}
          >
            <div
              className="relative bg-white rounded-lg p-2 max-w-4xl max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setEvidenceModal({ open: false, url: null, timestamp: null, location: null })}
                className="absolute top-2 right-2 bg-gray-200 hover:bg-gray-300 rounded-full p-1 z-10"
              >
                <Clear />
              </button>
              <img
                src={evidenceModal.url}
                alt="Evidence"
                className="max-w-full h-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23f3f4f6" width="200" height="200"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af">Image not found</text></svg>';
                }}
              />
              {/* Timestamp & Location Info */}
              <div className="mt-2 p-3 bg-gray-50 rounded text-sm">
                {evidenceModal.timestamp && (
                  <p className="mb-1">
                    <strong>Captured At:</strong>{" "}
                    {new Date(evidenceModal.timestamp).toLocaleString()}
                  </p>
                )}
                {evidenceModal.location?.latitude && (
                  <>
                    <p className="mb-1">
                      <strong>Location:</strong>{" "}
                      {evidenceModal.location.address ||
                        `${evidenceModal.location.latitude.toFixed(6)}, ${evidenceModal.location.longitude.toFixed(6)}`}
                      {evidenceModal.location.accuracy && (
                        <span className="text-gray-400 ml-2">
                          (accuracy: ~{Math.round(evidenceModal.location.accuracy)}m)
                        </span>
                      )}
                    </p>
                    <p>
                      <strong>Coordinates:</strong>{" "}
                      {evidenceModal.location.latitude.toFixed(6)}, {evidenceModal.location.longitude.toFixed(6)}
                    </p>
                  </>
                )}
                {!evidenceModal.timestamp && !evidenceModal.location?.latitude && (
                  <p className="text-gray-400 italic">No timestamp/location data available for this evidence.</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Bulk Approve Confirmation Modal */}
      {bulkApproveModal && (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-auto my-6 mx-auto max-w-md">
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                <div className="flex items-center justify-between py-1 px-3 border-b border-solid border-blueGray-200 rounded-t">
                  <h3 className="text-lg font-semibold mt-0 mb-0">
                    Approve Multiple Rake
                  </h3>
                </div>
                <div className="relative p-6 flex-auto">
                  <p className="text-gray-500 text-sm leading-relaxed mb-3">
                    Are you sure you want to approve{" "}
                    <strong>{selectedCommissionsForBulkDelete.length}</strong>{" "}
                    selected rake records?
                  </p>
                  <div className="max-h-32 overflow-y-auto">
                    {selectedCommissionsForBulkDelete.map(
                      (commission, index) => (
                        <p key={index} className="text-xs text-gray-600">
                          • ₹{commission.amount} -{" "}
                          {new Date(commission.date).toLocaleDateString()}
                        </p>
                      ),
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b">
                  <button
                    className="text-gray-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    type="button"
                    onClick={closeBulkApproveModal}
                  >
                    Cancel
                  </button>
                  <button
                    className="bg-cm-gold text-cm-gold-ink active:bg-cm-gold font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300"
                    type="button"
                    onClick={handleBulkApprove}
                    disabled={loadingBulkStatus}
                  >
                    Approve All
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      )}

      {/* Evidence Status Update Modal */}
      {statusModal && (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-auto my-6 mx-auto max-w-md">
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                <div className="flex items-center justify-between py-1 px-3 border-b border-solid border-blueGray-200 rounded-t">
                  <h3 className="text-lg font-semibold mt-0 mb-0">
                    {newStatus === "Approved" ? "Approve" : "Reject"} Rake
                    Record
                  </h3>
                </div>
                <div className="relative p-6 flex-auto">
                  <p className="text-gray-500 text-sm leading-relaxed mb-3">
                    Are you sure you want to{" "}
                    <strong>
                      {newStatus === "Approved" ? "approve" : "reject"}
                    </strong>{" "}
                    this rake record?
                  </p>
                  <p className="text-sm mb-2">
                    <strong>Amount:</strong> ₹
                    {selectedCommissionForStatus?.amount}
                  </p>
                  <p className="text-sm mb-4">
                    <strong>Date:</strong>{" "}
                    {selectedCommissionForStatus?.date
                      ? new Date(
                          selectedCommissionForStatus.date,
                        ).toLocaleDateString()
                      : "N/A"}
                  </p>
                  <div className="mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      Remarks (optional)
                    </label>
                    <textarea
                      value={statusRemarks}
                      onChange={(e) => setStatusRemarks(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      rows="2"
                      placeholder="Add remarks for this action..."
                    />
                  </div>
                </div>
                <div className="flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b">
                  <button
                    className="text-gray-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    type="button"
                    onClick={closeStatusModal}
                  >
                    Cancel
                  </button>
                  <button
                    className={`${newStatus === "Approved" ? "bg-green-500 active:bg-green-600" : "bg-orange-500 active:bg-orange-600"} text-white font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300`}
                    type="button"
                    onClick={handleStatusUpdate}
                    disabled={loadingStatusUpdate}
                  >
                    {newStatus}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      )}

      {/* Chip Distribution View Modal */}
      {chipViewModal.open && (
        <>
          {/* Modal Overlay */}
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
            onClick={() => setChipViewModal({ open: false, data: null })}
          >
            {/* Modal Container */}
            <div
              className="relative bg-white rounded-lg shadow-lg p-6 max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Chip Distribution</h3>
                <button
                  onClick={() => setChipViewModal({ open: false, data: null })}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ×
                </button>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 overflow-x-auto">
                {/* Chip Grid */}
                <div className="min-w-[600px] grid grid-cols-6 gap-4">
                  {[25000, 10000, 5000, 1000, 500, 100].map((denomination) => {
                    // Handle both Map and plain object from MongoDB
                    const chipData = chipViewModal.data;
                    let count = 0;

                    if (chipData instanceof Map) {
                      count =
                        chipData.get(denomination.toString()) ||
                        chipData.get(denomination) ||
                        0;
                    } else if (typeof chipData === "object") {
                      count =
                        chipData[denomination.toString()] ||
                        chipData[denomination] ||
                        0;
                    }

                    const isPresent = count > 0;

                    return (
                      <div
                        key={denomination}
                        className={`flex flex-col items-center space-y-2 p-3 rounded ${isPresent ? "bg-white shadow-sm border border-gray-200" : "opacity-50 grayscale"}`}
                      >
                        <span className="text-sm font-bold text-gray-700">
                          ₹{denomination.toLocaleString()}
                        </span>
                        <div
                          className={`flex items-center justify-center w-full py-2 rounded text-base font-mono font-medium ${isPresent ? "bg-blue-50 text-blue-800 border border-blue-100" : "bg-gray-100 text-gray-400 border border-gray-200"}`}
                        >
                          {count}
                        </div>
                        <span className="text-[10px] uppercase tracking-wide text-gray-400">
                          chips
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Total Value Summary */}
                <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500 font-medium">
                      Grand Total:
                    </span>
                    <span className="text-lg font-bold text-emerald-600">
                      ₹
                      {(() => {
                        const chipData = chipViewModal.data;
                        let total = 0;

                        if (chipData instanceof Map) {
                          chipData.forEach((count, denom) => {
                            total += parseInt(denom) * parseInt(count);
                          });
                        } else if (typeof chipData === "object") {
                          Object.entries(chipData).forEach(([denom, count]) => {
                            total += parseInt(denom) * parseInt(count);
                          });
                        }

                        return total.toLocaleString();
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Close Button */}
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setChipViewModal({ open: false, data: null })}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DailyCommission;
